// ==========================================
// src/pages/FreteEstado.jsx
// PÁGINA DE FRETE POR ESTADO — SEO LENDÁRIO PARA GOOGLE
// URL: /frete-mato-grosso, /frete-sao-paulo, etc.
// ✅ Schema.org: BreadcrumbList + CollectionPage + ItemList
// ✅ Open Graph dinâmico
// ✅ Canonical URL
// ==========================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { supabase } from '@/lib/supabase';
import {
  MapPin, Loader2, ArrowLeft, Truck, Package, ArrowRight, Calendar, Weight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

// Mapeamento de siglas para nomes completos
const estadosMap = {
  'AC': 'Acre', 'AL': 'Alagoas', 'AP': 'Amapá', 'AM': 'Amazonas',
  'BA': 'Bahia', 'CE': 'Ceará', 'DF': 'Distrito Federal', 'ES': 'Espírito Santo',
  'GO': 'Goiás', 'MA': 'Maranhão', 'MT': 'Mato Grosso', 'MS': 'Mato Grosso do Sul',
  'MG': 'Minas Gerais', 'PA': 'Pará', 'PB': 'Paraíba', 'PR': 'Paraná',
  'PE': 'Pernambuco', 'PI': 'Piauí', 'RJ': 'Rio de Janeiro', 'RN': 'Rio Grande do Norte',
  'RS': 'Rio Grande do Sul', 'RO': 'Rondônia', 'RR': 'Roraima', 'SC': 'Santa Catarina',
  'SP': 'São Paulo', 'SE': 'Sergipe', 'TO': 'Tocantins'
};

// Slug para sigla (inverso)
const slugToSigla = {};
Object.entries(estadosMap).forEach(([sigla, nome]) => {
  const slug = nome.toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  slugToSigla[slug] = sigla;
});

// Sigla para slug
const siglaToSlug = {};
Object.entries(estadosMap).forEach(([sigla, nome]) => {
  const slug = nome.toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  siglaToSlug[sigla] = slug;
});

const formatarValor = (valor) => {
  if (!valor || valor === 0) return 'A combinar';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
};

const formatarData = (dataStr) => {
  if (!dataStr) return 'Não informada';
  try {
    return new Date(dataStr).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dataStr;
  }
};

export default function FreteEstado() {
  const { estadoSlug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [cargas, setCargas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [estadoNome, setEstadoNome] = useState('');
  const [estadoSigla, setEstadoSigla] = useState('');

  // ==========================================
  // ✅ SEO LENDÁRIO — Schema.org completo
  // ==========================================
  useEffect(() => {
    const sigla = slugToSigla[estadoSlug];
    const nome = estadosMap[sigla] || estadoSlug;

    setEstadoSigla(sigla);
    setEstadoNome(nome);

    const urlAtual = `https://borafrete.com.br/frete-${estadoSlug}`;
    const imagemOg = 'https://borafrete.com.br/logoborinho45.png';

    // 1. Título
    document.title = `Fretes ${nome} | ${cargas.length} Cargas Disponíveis | BoraFrete`;

    // 2. Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content',
      `Encontre fretes e cargas para transporte em ${nome}. ` +
      `${cargas.length} cargas disponíveis para motoristas autônomos, caminhoneiros e transportadoras. ` +
      `Cargas saindo e chegando em ${nome}. Contrate via BoraFrete — sem mensalidade!`
    );

    // 3. Meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content',
      `fretes ${nome}, cargas ${nome}, transporte ${nome}, ` +
      `caminhão ${nome}, motorista ${nome}, frete ${sigla}, ` +
      `transportadora ${nome}, cargas para ${nome}, ` +
      `frete ${nome.toLowerCase()}, carga ${nome.toLowerCase()}, ` +
      `fretes disponíveis ${nome}, cargas disponíveis ${nome}`
    );

    // 4. Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', urlAtual);

    // 5. Open Graph
    const ogTags = [
      { property: 'og:title', content: `Fretes ${nome} | Cargas Disponíveis | BoraFrete` },
      { property: 'og:description', content: `Encontre ${cargas.length} fretes e cargas para transporte em ${nome}. Motoristas autônomos e transportadoras.` },
      { property: 'og:url', content: urlAtual },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: imagemOg },
      { property: 'og:site_name', content: 'BoraFrete' },
      { property: 'og:locale', content: 'pt_BR' },
    ];
    ogTags.forEach(tag => {
      let el = document.querySelector(`meta[property="${tag.property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', tag.property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // 6. Twitter Card
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: `Fretes ${nome} | BoraFrete` },
      { name: 'twitter:description', content: `Encontre fretes e cargas para transporte em ${nome}.` },
      { name: 'twitter:image', content: imagemOg },
    ];
    twitterTags.forEach(tag => {
      let el = document.querySelector(`meta[name="${tag.name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', tag.name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // 7. Schema.org — BreadcrumbList
    let schemaBreadcrumb = document.getElementById('schema-breadcrumb');
    if (!schemaBreadcrumb) {
      schemaBreadcrumb = document.createElement('script');
      schemaBreadcrumb.id = 'schema-breadcrumb';
      schemaBreadcrumb.type = 'application/ld+json';
      document.head.appendChild(schemaBreadcrumb);
    }
    schemaBreadcrumb.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://borafrete.com.br/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Buscar Cargas",
          "item": "https://borafrete.com.br/buscar-cargas"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": `Fretes ${nome}`,
          "item": urlAtual
        }
      ]
    });

    // 8. Schema.org — CollectionPage + ItemList
    let schemaCollection = document.getElementById('schema-collection');
    if (!schemaCollection) {
      schemaCollection = document.createElement('script');
      schemaCollection.id = 'schema-collection';
      schemaCollection.type = 'application/ld+json';
      document.head.appendChild(schemaCollection);
    }
    schemaCollection.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": `Fretes ${nome}`,
      "description": `Cargas disponíveis para transporte em ${nome}`,
      "url": urlAtual,
      "image": imagemOg,
      "about": {
        "@type": "Place",
        "name": nome,
        "address": {
          "@type": "PostalAddress",
          "addressRegion": sigla,
          "addressCountry": "BR"
        }
      },
      "mainEntity": {
        "@type": "ItemList",
        "itemListElement": cargas.slice(0, 10).map((carga, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "JobPosting",
            "title": `Frete de ${carga.tipo_carga || 'Carga'} - ${carga.origem_cidade} para ${carga.destino_cidade}`,
            "description": `Frete disponível: ${carga.tipo_carga || 'Carga'} saindo de ${carga.origem_cidade}/${carga.origem_estado} para ${carga.destino_cidade}/${carga.destino_estado}. Peso: ${carga.peso_valor} ${carga.peso_unidade || 'ton'}.`,
            "url": `https://borafrete.com.br/frete/${carga.slug || carga.id}`,
            "datePosted": carga.created_at,
            "jobLocation": {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": carga.destino_cidade,
                "addressRegion": carga.destino_estado,
                "addressCountry": "BR"
              }
            }
          }
        }))
      }
    });

    // 9. Schema.org — WebPage
    let schemaWebPage = document.getElementById('schema-webpage');
    if (!schemaWebPage) {
      schemaWebPage = document.createElement('script');
      schemaWebPage.id = 'schema-webpage';
      schemaWebPage.type = 'application/ld+json';
      document.head.appendChild(schemaWebPage);
    }
    schemaWebPage.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": `Fretes ${nome}`,
      "description": `Encontre fretes e cargas para transporte em ${nome}`,
      "url": urlAtual,
      "breadcrumb": {
        "@id": "https://borafrete.com.br/#breadcrumb"
      }
    });

    // Cleanup
    return () => {
      document.title = 'BoraFrete - Conectando Cargas e Motoristas';
      const schemas = ['schema-breadcrumb', 'schema-collection', 'schema-webpage'];
      schemas.forEach(id => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [estadoSlug, cargas.length, estadoNome, estadoSigla]);

  // Buscar cargas do estado
  useEffect(() => {
    const buscarCargas = async () => {
      if (!estadoSigla) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        const { data, error } = await supabase
          .from('cargas')
          .select('*')
          .eq('status', 'disponivel')
          .eq('aprovada_admin', true)
          .or(`origem_estado.eq.${estadoSigla},destino_estado.eq.${estadoSigla}`)
          .order('carga_ficticia', { ascending: true })
          .order('created_at', { ascending: false });

        if (error) throw error;

        setCargas(data || []);
      } catch (err) {
        console.error('Erro ao buscar cargas:', err);
        toast.error('Erro ao carregar cargas');
      } finally {
        setIsLoading(false);
      }
    };

    buscarCargas();
  }, [estadoSigla]);

  const handleVerDetalhes = (cargaId) => {
    navigate(`/carga/${cargaId}`);
  };

  // Se estado não existe, redireciona
  if (!estadoSigla && !isLoading) {
    navigate('/buscar-cargas');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="mb-4">
          <ol className="flex items-center gap-2 text-sm text-gray-500">
            <li>
              <Link to="/" className="hover:text-blue-600 transition-colors">Home</Link>
            </li>
            <li><ArrowRight className="w-3 h-3" /></li>
            <li>
              <Link to="/buscar-cargas" className="hover:text-blue-600 transition-colors">Buscar Cargas</Link>
            </li>
            <li><ArrowRight className="w-3 h-3" /></li>
            <li className="text-gray-900 font-medium">Fretes {estadoNome}</li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/buscar-cargas')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para busca
          </Button>

          <div className="bg-gradient-to-r from-blue-800 to-blue-600 rounded-xl p-6 text-white">
            <div className="flex items-center gap-3 mb-2">
              <MapPin className="w-8 h-8" />
              <h1 className="text-2xl font-bold">
                Fretes {estadoNome}
              </h1>
            </div>
            <p className="text-blue-100">
              {isLoading ? 'Carregando...' : `${cargas.length} ${cargas.length === 1 ? 'carga disponível' : 'cargas disponíveis'} para transporte em ${estadoNome} e para ${estadoNome}`}
            </p>
          </div>
        </div>

        {/* Lista de cargas */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin mb-3" />
            <p className="text-sm text-gray-500">Carregando cargas...</p>
          </div>
        ) : cargas.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Truck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Nenhuma carga em {estadoNome} no momento
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Novas cargas aparecem diariamente. Volte mais tarde!
            </p>
            <Button
              onClick={() => navigate('/buscar-cargas')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Ver todas as cargas
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {cargas.map((carga) => (
              <article
                key={carga.id}
                className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg hover:border-blue-200 transition-all cursor-pointer"
                onClick={() => handleVerDetalhes(carga.id)}
                itemScope
                itemType="https://schema.org/JobPosting"
              >
                <meta itemProp="title" content={`Frete de ${carga.tipo_carga || 'Carga'} - ${carga.origem_cidade} para ${carga.destino_cidade}`} />
                <meta itemProp="datePosted" content={carga.created_at} />
                
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-700">
                      {carga.tipo_carga || 'Carga'}
                    </Badge>
                    <Badge className="bg-emerald-100 text-emerald-700">
                      Disponível
                    </Badge>
                  </div>
                  {carga.valor_total ? (
                    <span className="text-sm font-bold text-blue-600">
                      {formatarValor(carga.valor_total)}
                    </span>
                  ) : carga.valor_frete ? (
                    <span className="text-sm font-bold text-blue-600">
                      {formatarValor(carga.valor_frete)}
                    </span>
                  ) : null}
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex-1" itemProp="jobLocation" itemScope itemType="https://schema.org/Place">
                    <meta itemProp="name" content={`${carga.origem_cidade}, ${carga.origem_estado}`} />
                    <p className="font-bold text-gray-900">
                      {carga.origem_cidade} - {carga.origem_estado}
                    </p>
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatarData(carga.data_coleta)}
                    </p>
                  </div>
                  <div className="text-gray-400">→</div>
                  <div className="flex-1">
                    <p className="font-bold text-gray-900">
                      {carga.destino_cidade} - {carga.destino_estado}
                    </p>
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatarData(carga.data_entrega)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 text-xs text-gray-600">
                  <span className="flex items-center gap-1">
                    <Weight className="w-3.5 h-3.5" />
                    {carga.peso_valor || '-'} {carga.peso_unidade || 'ton'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Package className="w-3.5 h-3.5" />
                    {carga.tipo_veiculo || 'Não informado'}
                  </span>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* CTA Final */}
        <div className="mt-8 text-center bg-blue-50 rounded-xl p-6 border border-blue-100">
          <p className="text-gray-700 mb-3">
            Quer receber alertas de novas cargas em {estadoNome}?
          </p>
          <Button
            onClick={() => navigate('/cadastro-motorista')}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Truck className="w-4 h-4 mr-2" />
            Cadastrar como Motorista
          </Button>
        </div>
      </div>
    </div>
  );
}